import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus } from "lucide-react";
import { api } from "../../api/client";
import { btnPrimary, type as text, theme } from "../../theme";
import { SheetCard, StatusTag, RulerProgress } from "../../components/Drawing";
import { LoadingBlock } from "../../components/Spinner";
import { NextSteps } from "../../components/NextSteps";
import { EntityTagsRow } from "../../components/EntityTags";
import { ReviewMinutesReader } from "../../components/ReviewMinutesReader";
import { useDeepLinkScroll } from "../../hooks/useDeepLinkScroll";
import { EmptyState, Field, ModalHeader, ModalOverlay, StatusPill, input, textarea, secondaryBtn, dangerBtn, toDateInput, dateInputToIso, fmtDateOnly, todayInput } from "../resilience/shared";
import { AttentionList, FrameworkPick, Meta, OwnerField, attentionFor, msRef, personName, standardsOf, useMembers, useMsEnrolment, useMsPermissions, type Person } from "./shared";
import { attempt, useClearErrorOnDialog, useMsOverview, useRegister } from "./useRegister";

interface Decision { id: string; kind: string; text: string; ownerName: string | null; dueDate: string | null; correctiveActionId: string | null }
interface Review {
  id: string; displayId: number | null; title: string; frameworkIds: string[]; scheduledDate: string; heldAt: string | null; status: string;
  chairUserId: string | null; chair: Person | null; chairName: string | null; attendees: string | null;
  inputs: Record<string, { covered: boolean; notes: string }>; conclusions: string | null; decisions: Decision[];
  coverage: { required: string[]; covered: string[]; missing: string[] };
  /** Set the first time the review was held and never cleared: such a review is retained, never deleted. */
  firstHeldAt: string | null;
}
interface Vocabulary {
  inputs: Array<{ key: string; label: string; frameworks: string[] }>;
  decisionKinds: Array<{ id: string; label: string }>;
}
interface Digest { since: string; digest: Record<string, string[]> }

type Form = {
  title: string; frameworkIds: string[]; scheduledDate: string; heldAt: string; status: string; chairUserId: string; chairName: string;
  attendees: string; inputs: Record<string, { covered: boolean; notes: string }>; conclusions: string; decisions: Decision[];
};

export function ManagementReviews() {
  const { data, loading, error, reload } = useRegister<{ reviews: Review[]; vocabulary: Vocabulary }>("/management-system/reviews");
  const overview = useMsOverview();
  const enrolled = useMsEnrolment();
  const members = useMembers();
  const { canWrite } = useMsPermissions();
  const [params] = useSearchParams();
  const [editing, setEditing] = useState<{ id: string | null; status: string; form: Form } | null>(null);
  const [digest, setDigest] = useState<Digest | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  useDeepLinkScroll(params.get("review"), !!data && !overview.loading);
  useClearErrorOnDialog(setFormError, !!editing);

  if (loading) return <LoadingBlock />;
  if (error || !data) return <p style={{ ...text.body, color: theme.color.dangerText }}>{error}</p>;
  const refresh = () => Promise.all([reload(), overview.reload()]);
  const kindLabel = (id: string) => data.vocabulary.decisionKinds.find((k) => k.id === id)?.label ?? id;

  const open = (r: Review | null) => {
    setDigest(null);
    if (!r) {
      setEditing({ id: null, status: "planned", form: { title: "Management review", frameworkIds: [], scheduledDate: todayInput(30), heldAt: "", status: "planned", chairUserId: "", chairName: "", attendees: "", inputs: {}, conclusions: "", decisions: [] } });
      return;
    }
    setEditing({
      id: r.id, status: r.status,
      form: {
        title: r.title, frameworkIds: r.frameworkIds, scheduledDate: toDateInput(r.scheduledDate), heldAt: toDateInput(r.heldAt), status: r.status,
        chairUserId: r.chairUserId ?? "", chairName: r.chairName ?? "", attendees: r.attendees ?? "", inputs: r.inputs ?? {}, conclusions: r.conclusions ?? "", decisions: r.decisions ?? [],
      },
    });
    void api.get<Digest>(`/management-system/reviews/${r.id}/digest`).then(setDigest).catch(() => setDigest(null));
  };

  const save = async (statusOverride?: string) => {
    if (!editing) return;
    const f = editing.form;
    const body = {
      ...f, status: statusOverride ?? f.status, scheduledDate: dateInputToIso(f.scheduledDate), heldAt: dateInputToIso(f.heldAt),
      chairUserId: f.chairUserId || null, chairName: f.chairName || null, attendees: f.attendees || null, conclusions: f.conclusions || null,
      decisions: f.decisions.map((d) => ({ id: d.id || undefined, kind: d.kind, text: d.text, ownerName: d.ownerName || null, dueDate: d.dueDate || null })),
    };
    if (await attempt(() => (editing.id ? api.put(`/management-system/reviews/${editing.id}`, body) : api.post("/management-system/reviews", body)), setFormError, "Could not save the review")) {
      setEditing(null);
      await refresh();
    }
  };

  const raise = async (r: Review, d: Decision) => {
    if (await attempt(() => api.post(`/management-system/reviews/${r.id}/decisions/${d.id}/action`, {}), setFormError, "Could not raise the corrective action")) await refresh();
  };
  const remove = async (r: Review) => {
    if (!window.confirm(`Delete ${msRef("review", r.displayId)} ${r.title}?`)) return;
    if (await attempt(() => api.del(`/management-system/reviews/${r.id}`), setFormError, "Could not delete the review")) await refresh();
  };

  const f = editing?.form;
  const set = (patch: Partial<Form>) => editing && setEditing({ ...editing, form: { ...editing.form, ...patch } });
  const formFrameworks = f ? (f.frameworkIds.length ? f.frameworkIds : enrolled) : [];
  const required = data.vocabulary.inputs.filter((i) => i.frameworks.some((fw) => (formFrameworks as string[]).includes(fw)));

  return (
    <>
      <AttentionList items={attentionFor(overview.data?.attention ?? [], "ms_review")} />
      {formError && !editing && <p style={{ ...text.body, color: theme.color.dangerText }}>{formError}</p>}
      <SheetCard label="Management reviews" reference="9.3" actions={canWrite ? <button type="button" style={secondaryBtn} onClick={() => open(null)}><Plus size={12} /> Review</button> : undefined}>
        {data.reviews.length === 0 ? (
          <EmptyState
            title="No management reviews recorded"
            detail="Top management reviews the management system at planned intervals. Schedule a review; when it is held, record which of the required inputs were considered, the conclusion and the decisions — the platform gathers what its registers already hold for each input."
            action={canWrite ? <button type="button" style={btnPrimary} onClick={() => open(null)}>Schedule a review</button> : undefined}
          />
        ) : (
          <div style={{ display: "grid", gap: 8 }}>
            {data.reviews.map((r) => (
              <div key={r.id} data-deeplink={r.id} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: "10px 12px", background: "var(--c-panel)" }}>
                <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                  <span style={{ ...text.ref, color: theme.color.ref }}>{msRef("review", r.displayId)}</span>
                  <span style={text.itemTitle}>{r.title}</span>
                  <StatusPill value={r.status === "held" ? "completed" : r.status} label={r.status === "held" ? "Held" : undefined} />
                  {r.status === "held" && r.coverage.missing.length > 0 && <StatusTag tone="partial">{r.coverage.missing.length} input{r.coverage.missing.length === 1 ? "" : "s"} not considered</StatusTag>}
                  {canWrite && (
                    <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
                      <button type="button" style={secondaryBtn} onClick={() => open(r)}>{r.status === "planned" ? "Record the review" : "Open"}</button>
                      {r.status !== "held" && !r.firstHeldAt && <button type="button" style={dangerBtn} onClick={() => void remove(r)}>Delete</button>}
                    </span>
                  )}
                </div>
                <Meta>
                  <span>{r.heldAt ? `Held ${fmtDateOnly(r.heldAt)}` : `Scheduled ${fmtDateOnly(r.scheduledDate)}`}</span>
                  <span>Chair: {personName(r.chair, r.chairName)}</span>
                  <span>Standards: {standardsOf(r.frameworkIds, enrolled)}</span>
                </Meta>
                <div style={{ maxWidth: 360, marginTop: 6 }}>
                  <RulerProgress value={r.coverage.covered.length} max={Math.max(1, r.coverage.required.length)} label="Required inputs considered" height={10} />
                  <span style={text.meta}>{r.coverage.covered.length} of {r.coverage.required.length} required inputs considered</span>
                </div>
                {r.conclusions && <p style={{ ...text.body, margin: "6px 0 0", whiteSpace: "pre-line" }}><strong>Conclusion:</strong> {r.conclusions}</p>}
                {r.decisions.length > 0 && (
                  <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
                    {r.decisions.map((d) => (
                      <li key={d.id} style={text.body}>
                        <span style={text.meta}>{kindLabel(d.kind)}: </span>{d.text}
                        {d.ownerName && <span style={text.hint}> · {d.ownerName}</span>}
                        {d.dueDate && <span style={text.hint}> · due {fmtDateOnly(d.dueDate)}</span>}
                        {d.correctiveActionId
                          ? <a href={`/improvement?action=${d.correctiveActionId}`} style={{ marginLeft: 8, color: theme.color.ref }}>Corrective action</a>
                          : canWrite && r.status === "held" && <button type="button" style={{ ...secondaryBtn, marginLeft: 8, padding: "2px 8px" }} onClick={() => void raise(r, d)}>Raise action</button>}
                      </li>
                    ))}
                  </ul>
                )}
                <EntityTagsRow entity={{ kind: "ms_review", id: r.id }} readOnly={!canWrite} style={{ marginTop: 6 }} />
                {canWrite && r.status !== "cancelled" && <ReviewMinutesReader reviewId={r.id} onChanged={() => void refresh()} />}
              </div>
            ))}
          </div>
        )}
      </SheetCard>

      {editing && f && (
        <ModalOverlay wide onClose={() => setEditing(null)}>
          <ModalHeader title={editing.id ? `${msRef("review", data.reviews.find((r) => r.id === editing.id)?.displayId)} ${f.title}` : "Schedule a management review"} onClose={() => setEditing(null)} />
          <div style={{ padding: 20, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            {/* Ticks as the form is filled in: what 9.3 expects of a review. The held-only items show once the review exists to be
                recorded, and turn urgent on a held review because the server refuses that save without them. */}
            <div style={{ gridColumn: "1 / -1" }}>
              {(() => {
                const uncovered = required.filter((i) => !f.inputs[i.key]?.covered).length;
                const held = editing.status === "held";
                const decisionsIncomplete = f.decisions.filter((d) => !d.ownerName?.trim() || !d.dueDate).length;
                return (
                  <NextSteps items={[
                    { done: !!f.title.trim(), label: "Give the review a title" },
                    { done: !!f.scheduledDate, label: "Set the scheduled date", hint: "9.3: reviews happen at planned intervals." },
                    { done: !!(f.chairUserId || f.chairName.trim()), label: "Name the chair", hint: "9.3: top management reviews the management system." },
                    { done: !!f.attendees.trim(), label: "List the attendees" },
                    ...(editing.id ? [
                      { done: uncovered === 0, label: uncovered ? `Consider every required input (${uncovered} still open)` : "Consider every required input", hint: "9.3.2 lists the inputs the review must take into account." },
                      { done: !!f.heldAt, label: "Give the date it was held", urgent: held },
                      { done: !!f.conclusions.trim(), label: "Record the conclusion", hint: "On the continuing suitability, adequacy and effectiveness of the management system.", urgent: held },
                      ...(f.decisions.length ? [{ done: decisionsIncomplete === 0, label: decisionsIncomplete ? `Give every decision an owner and a due date (${decisionsIncomplete} still open)` : "Give every decision an owner and a due date" }] : []),
                    ] : []),
                  ]} />
                );
              })()}
            </div>
            <Field label="Title" span2><input style={input} maxLength={300} value={f.title} onChange={(e) => set({ title: e.target.value })} /></Field>
            <Field label="Scheduled for"><input type="date" style={input} value={f.scheduledDate} onChange={(e) => set({ scheduledDate: e.target.value })} /></Field>
            <Field label="Held on" help="Recording a date here and saving as held closes the calendar task."><input type="date" style={input} max={todayInput()} value={f.heldAt} onChange={(e) => set({ heldAt: e.target.value })} /></Field>
            <OwnerField label="Chair (top management)" userId={f.chairUserId} name={f.chairName} members={members} onChange={(o) => set({ chairUserId: o.userId, chairName: o.name })} />
            <Field label="Attendees"><input style={input} value={f.attendees} onChange={(e) => set({ attendees: e.target.value })} /></Field>
            <FrameworkPick allowed={enrolled} value={f.frameworkIds} onChange={(ids) => set({ frameworkIds: ids })} />

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ ...text.sectionTitle, marginTop: 6 }}>Inputs the standards require</div>
              <p style={{ ...text.hint, margin: "0 0 8px" }}>
                Tick each input once the review has considered it and note what was concluded.{digest ? ` Beneath each, what the platform's registers record since ${fmtDateOnly(digest.since)}.` : ""}
              </p>
              <div style={{ display: "grid", gap: 8 }}>
                {required.map((i) => {
                  const v = f.inputs[i.key] ?? { covered: false, notes: "" };
                  const facts = digest?.digest[i.key] ?? [];
                  return (
                    <div key={i.key} style={{ border: "1px solid var(--c-border)", borderRadius: 3, padding: 8 }}>
                      <label style={{ display: "flex", gap: 8, alignItems: "flex-start", ...text.body, fontWeight: 600 }}>
                        <input type="checkbox" checked={v.covered} onChange={(e) => set({ inputs: { ...f.inputs, [i.key]: { ...v, covered: e.target.checked } } })} style={{ marginTop: 3 }} />
                        {i.label}
                      </label>
                      {facts.length > 0 && <ul style={{ margin: "4px 0 4px 24px", padding: 0 }}>{facts.map((line, n) => <li key={n} style={text.hint}>{line}</li>)}</ul>}
                      {digest && facts.length === 0 && <div style={{ ...text.meta, marginLeft: 24 }}>Nothing held in the platform for this input — record it in the notes.</div>}
                      <textarea style={{ ...textarea, marginTop: 4 }} rows={2} placeholder="What the review concluded" value={v.notes} onChange={(e) => set({ inputs: { ...f.inputs, [i.key]: { ...v, notes: e.target.value } } })} />
                    </div>
                  );
                })}
              </div>
            </div>

            <Field label="Conclusion" span2 help="On the continuing suitability, adequacy and effectiveness of the management system. Required to record the review as held.">
              <textarea style={textarea} rows={3} value={f.conclusions} onChange={(e) => set({ conclusions: e.target.value })} />
            </Field>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={{ ...text.sectionTitle, marginTop: 6 }}>Decisions and actions</div>
              <div style={{ display: "grid", gap: 6 }}>
                {f.decisions.map((d, n) => (
                  <div key={d.id || n} style={{ display: "grid", gridTemplateColumns: "160px 1fr 140px 140px auto", gap: 6, alignItems: "start" }}>
                    <select style={input} value={d.kind} onChange={(e) => set({ decisions: f.decisions.map((x, m) => (m === n ? { ...x, kind: e.target.value } : x)) })}>
                      {data.vocabulary.decisionKinds.map((k) => <option key={k.id} value={k.id}>{k.label}</option>)}
                    </select>
                    <input style={input} value={d.text} placeholder="Decision" onChange={(e) => set({ decisions: f.decisions.map((x, m) => (m === n ? { ...x, text: e.target.value } : x)) })} />
                    <input style={input} value={d.ownerName ?? ""} placeholder="Owner" onChange={(e) => set({ decisions: f.decisions.map((x, m) => (m === n ? { ...x, ownerName: e.target.value } : x)) })} />
                    <input type="date" style={input} value={d.dueDate ?? ""} onChange={(e) => set({ decisions: f.decisions.map((x, m) => (m === n ? { ...x, dueDate: e.target.value } : x)) })} />
                    <button type="button" style={dangerBtn} disabled={!!d.correctiveActionId} title={d.correctiveActionId ? "A corrective action was raised from it" : undefined} onClick={() => set({ decisions: f.decisions.filter((_, m) => m !== n) })}>Remove</button>
                  </div>
                ))}
              </div>
              <button type="button" style={{ ...secondaryBtn, marginTop: 6 }} onClick={() => set({ decisions: [...f.decisions, { id: "", kind: "improvement", text: "", ownerName: null, dueDate: null, correctiveActionId: null }] })}><Plus size={12} /> Decision</button>
            </div>

            {formError && <p style={{ gridColumn: "1 / -1", margin: 0, ...text.body, color: theme.color.dangerText }}>{formError}</p>}
            <div style={{ gridColumn: "1 / -1", display: "flex", justifyContent: "flex-end", gap: 8, flexWrap: "wrap" }}>
              <button type="button" style={secondaryBtn} onClick={() => setEditing(null)}>Cancel</button>
              {editing.status === "planned" && editing.id && <button type="button" style={secondaryBtn} onClick={() => void save("cancelled")}>Cancel the review</button>}
              {editing.status === "held" && <button type="button" style={secondaryBtn} onClick={() => void save("planned")}>Reopen</button>}
              <button type="button" style={secondaryBtn} disabled={!f.title.trim()} onClick={() => void save(editing.status)}>Save</button>
              {editing.status === "cancelled" && <button type="button" style={secondaryBtn} onClick={() => void save("planned")}>Re-plan</button>}
              {editing.status === "planned" && editing.id && <button type="button" style={btnPrimary} disabled={!f.heldAt || !f.conclusions.trim()} onClick={() => void save("held")}>Record as held</button>}
            </div>
          </div>
        </ModalOverlay>
      )}
    </>
  );
}
